import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import Header from "@/components/Header";
import PersonalMode from "@/components/PersonalMode";
import BusinessMode from "@/components/BusinessMode";
import DispatchLog from "@/components/DispatchLog";
import { BetaPanel } from "@/components/BetaPanel";
import { TrialBanner } from "@/components/TrialBanner";
import { BundleBanner } from "@/components/BundleBanner";
import { OnboardingChecklist } from "@/components/OnboardingChecklist";
import { useScope } from "@/context/ScopeContext";
import { useTheme } from "@/context/ThemeContext";

export default function Dashboard() {
  const [searchParams] = useSearchParams();
  const tabParam = searchParams.get("tab");
  const initialTab = ["miles", "moneyflex"].includes(tabParam) ? tabParam : "reminders";
  const [mode, setMode] = useState(searchParams.get("mode") === "business" ? "business" : "personal");
  const { setScope } = useScope();
  const { cc } = useTheme();
  const [tasks, setTasks] = useState([]);
  const [clients, setClients] = useState([]);
  const [dispatches, setDispatches] = useState([]);
  const [dispatchOpen, setDispatchOpen] = useState(false);

  useEffect(() => {
    document.body.setAttribute("data-mode", mode);
    setScope(mode);
  }, [mode, setScope]);

  useEffect(() => { setMode(searchParams.get("mode") === "business" ? "business" : "personal"); }, [searchParams]);

  useEffect(() => () => setScope("all"), [setScope]);

  const loadTasks = useCallback(() => api.get("/tasks").then((r) => setTasks(r.data)).catch(() => {}), []);
  const loadClients = useCallback(() => api.get("/clients").then((r) => setClients(r.data)).catch(() => {}), []);
  const loadDispatches = useCallback(() => api.get("/dispatches").then((r) => setDispatches(r.data)).catch(() => {}), []);

  const loadAll = useCallback(() => {
    loadTasks();
    loadClients();
    loadDispatches();
  }, [loadTasks, loadClients, loadDispatches]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const seedDemo = async () => {
    await api.post("/demo/seed");
    toast.success("Demo data loaded");
    loadAll();
  };
  const resetDemo = async () => {
    await api.post("/demo/reset");
    toast.success("All data cleared");
    loadAll();
  };

  const overdueCount = tasks.filter((t) => t.kind === "task" && !t.completed && (((t.next_run ? t.next_run.slice(0, 10) : t.start_date) || "") < new Date().toISOString().slice(0, 10))).length;

  return (
    <div className={`min-h-screen app-canvas ${cc ? "cc" : ""}`} data-testid="dashboard-root">
      <Header
        mode={mode}
        setMode={setMode}
        overdueCount={overdueCount}
        onOpenDispatch={() => setDispatchOpen(true)}
        dispatchCount={dispatches.length}
        onSeed={seedDemo}
        onReset={resetDemo}
      />

      <main>
        <TrialBanner />
        <BundleBanner />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-6"><BetaPanel /></div>

        {mode === "personal" ? (
          <>
            <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-8 sm:pt-12 -mb-4"><OnboardingChecklist tasks={tasks} /></div>
            <PersonalMode tasks={tasks} reload={loadTasks} initialTab={initialTab} />
          </>
        ) : (
          <BusinessMode clients={clients} reload={loadClients} />
        )}
      </main>

      <DispatchLog open={dispatchOpen} onClose={() => setDispatchOpen(false)} onCountChange={loadDispatches} />
    </div>
  );
}
